import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function randomString(bytes = 16) {
  const a = new Uint8Array(bytes);
  window.crypto.getRandomValues(a);
  return Array.from(a).map((x) => x.toString(16).padStart(2, '0')).join('');
}

function loadAppleSdk() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    let script = document.getElementById('appleid-auth-script');
    if (script) {
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => reject(new Error('apple-sdk-load-failed')));
      return;
    }
    script = document.createElement('script');
    script.id = 'appleid-auth-script';
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('apple-sdk-load-failed'));
    document.head.appendChild(script);
  });
}

/**
 * AppleSignInButton — mirrors GoogleSignInButton API.
 *
 * Modes:
 *   - "auth" (default): sign-in / sign-up. Calls POST /api/auth/apple/direct
 *     and stores the JWT + user in localStorage on success.
 *   - "link": link Apple to an already-authenticated user. Requires an app
 *     JWT to already be in localStorage.
 *
 * Props:
 *   - mode: "auth" | "link"
 *   - onSuccess: (data) => void
 *   - onError:   (err)  => void
 *   - locale: "ar" | "en"
 *   - label: optional string override for the button label
 */
export default function AppleSignInButton({
  mode = 'auth',
  onSuccess,
  onError,
  locale = 'ar',
  label,
}) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const initedRef = useRef(false);

  const isAr = locale === 'ar';

  useEffect(() => {
    let cancelled = false;
    loadAppleSdk()
      .then(() => {
        if (cancelled) return;
        try {
          if (!initedRef.current && window.AppleID) {
            window.AppleID.auth.init({
              clientId: APPLE_CLIENT_ID,
              scope: 'name email',
              redirectURI: APPLE_REDIRECT_URI,
              usePopup: true,
            });
            initedRef.current = true;
          }
          setReady(true);
        } catch (e) {
          setReady(false);
        }
      })
      .catch(() => setReady(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (!ready || !window.AppleID) {
      toast.error(isAr ? 'خدمة Apple غير جاهزة، حاول مجدداً' : 'Apple service not ready');
      return;
    }
    setBusy(true);
    const nonce = randomString();
    const state = randomString();
    try {
      // Re-init with fresh nonce/state on every click (single-use).
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        state,
        nonce,
        usePopup: true,
      });

      const result = await window.AppleID.auth.signIn();
      const authz = result?.authorization || {};
      const idToken = authz.id_token;
      const code = authz.code;
      const returnedState = authz.state;

      if (!idToken) {
        throw new Error('no-id-token');
      }
      if (returnedState && returnedState !== state) {
        throw new Error('state-mismatch');
      }

      const url =
        mode === 'link'
          ? `${BACKEND_URL}/api/auth/apple/direct/link`
          : `${BACKEND_URL}/api/auth/apple/direct`;
      const headers = { 'Content-Type': 'application/json' };
      if (mode === 'link') {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
          setBusy(false);
          return;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const body = {
        id_token: idToken,
        code,
        state: returnedState,
        nonce,
      };
      if (result.user) {
        body.user = {
          name: result.user.name || null,
          email: result.user.email || null,
        };
      }

      const res = await axios.post(url, body, { headers });

      if (mode === 'auth') {
        if (res.data?.access_token) {
          localStorage.setItem('token', res.data.access_token);
        }
        if (res.data?.user) {
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
      }

      onSuccess?.(res.data);
    } catch (err) {
      // Apple SDK returns { error: 'popup_closed_by_user' } etc.
      const appleErr = err?.error;
      if (appleErr === 'popup_closed_by_user' || appleErr === 'user_cancelled_authorize') {
        onError?.(err);
        return; // silent — user cancelled
      }
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === 'object') {
        msg = isAr ? detail.message_ar : detail.message_en;
      } else {
        msg = detail || (isAr ? 'فشل تسجيل الدخول بحساب Apple' : 'Apple sign-in failed');
      }
      toast.error(msg);
      onError?.(err);
    } finally {
      setBusy(false);
    }
  }, [ready, mode, isAr, onSuccess, onError]);

  const defaultLabel = busy
    ? isAr
      ? 'جارِ الاتصال…'
      : 'Connecting…'
    : mode === 'link'
    ? isAr
      ? 'ربط حساب Apple'
      : 'Link Apple account'
    : isAr
    ? 'المتابعة باستخدام Apple'
    : 'Continue with Apple';

  return (
    <button
      type="button"
      data-testid={`apple-signin-btn-${mode}`}
      onClick={handleClick}
      disabled={!ready || busy}
      className="w-full h-12 inline-flex items-center justify-center gap-2.5 px-4 rounded-xl bg-black text-white text-[15px] font-medium leading-none border border-black shadow-[0_1px_2px_rgba(0,0,0,0.15)] hover:bg-neutral-900 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
    >
      {/* Apple logo — official SVG */}
      <svg
        aria-hidden="true"
        viewBox="0 0 384 512"
        width="18"
        height="18"
        fill="currentColor"
        style={{ marginTop: -2 }}
      >
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.9 106.9c22.9-27.2 21.2-63.2 20.6-77.2-23.4 1.4-50.5 15.6-66.9 34.6-18 20.3-28.6 45.4-26.3 71.5 25.6 2 47.3-14 72.6-28.9z" />
      </svg>
      <span>{label || defaultLabel}</span>
    </button>
  );
}
